import React, { useState, useEffect } from 'react';
import { isEmpty, xor } from 'lodash';
import { X, Check } from 'react-feather';
import classNames from 'classnames';
import { useFetch } from 'hooks';
import {
  HEADING_VARIANTS,
  Heading,
  TEXT_VARIANTS,
  Text,
} from 'components/ui/Typography';
import request from 'utils/request_ajax';
import QuestionElementPrompt from 'components/Questions/shared/QuestionElementPrompt';
import Button from 'components/ui/Button';
import Error from 'components/InPersonTraining/shared/Error';
import Skeleton from 'ui/Skeleton';
import ItemChoice from './ItemChoice';
import MatchingChoice from './MatchingChoice';
import { AssistiveText, ASSISTIVE_TEXT_STATES } from './AssistiveText';

const calculatedScore = choices => {
  const numerator = choices.filter(choice => choice.correct).length;
  const isCorrect =
    choices.filter(choice => choice.correct).length === choices.length;
  const denominator = choices.length;
  let textToDisplay;
  let icon;
  if (isCorrect) {
    textToDisplay = TEXT_VARIANTS.INSTRUX_SEMIBOLD_SUCCESS;
    icon = <Check className="mr-4" />;
  } else {
    textToDisplay = TEXT_VARIANTS.INSTRUX_ERROR;
    icon = <X className="mr-4" />;
  }
  return (
    <Text className="d-f ai-c mb-20" variant={textToDisplay}>
      {icon}
      {numerator} / {denominator} {t('grades.correct')}
    </Text>
  );
};
const MatchingShow = ({ questionId }) => {
  const [pairs, setPairs] = useState([]);
  const [active, setActive] = useState();
  const [choices, setChoices] = useState([]);
  const [matches, setMatches] = useState([]);
  const [assistiveStatus, setAssistiveStatus] = useState(null);
  const [isShowingResults, setIsShowingResults] = useState(false);
  const {
    data: matchingResult,
    loading,
    error,
  } = useFetch(
    `${window.location.pathname}/question/${questionId}${window.location.search}`,
  );
  const onClickItemChoice = index => {
    if (active === index || pairs[index]) return;
    setActive(index);
    setAssistiveStatus({
      state: ASSISTIVE_TEXT_STATES.ITEM_ACTIVE,
      item: matchingResult.data.choices[index].item,
    });
  };

  const removeFromArray = (items, elementToRemove) => {
    const indexToRemove = items.indexOf(elementToRemove);
    return [
      ...items.slice(0, indexToRemove),
      ...items.slice(indexToRemove + 1),
    ];
  };

  const removeMatch = matchToRemove => {
    setMatches(prevMatches => {
      return removeFromArray(prevMatches, matchToRemove);
    });
    setActive();
  };

  const addMatch = matchToReAdd => {
    setMatches(prevMatches => [...prevMatches, matchToReAdd]);
    setActive();
  };

  const replaceMatch = (matchToReAdd, pieceToRemove) => {
    setMatches(prevMatches => {
      const updatedArray = removeFromArray(prevMatches, pieceToRemove);
      return [...updatedArray, matchToReAdd];
    });
    setActive();
  };

  const isPreview = matchingResult?.meta?.previewing;
  const choiceClassNames = classNames('mb-12', {
    'disabled pe-none': isPreview,
  });

  const compareMatchIdOrText = (matchesArray, givenMatch, correctMatchId) => {
    if (
      givenMatch.id === correctMatchId ||
      givenMatch.match.toUpperCase() ===
        matchesArray.find(({ id }) => id === correctMatchId).match.toUpperCase()
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!loading && !error) {
      const orderedCorrectChoices = matchingResult?.data?.choices;
      if (matchingResult.meta.finished) {
        // progress finished so show given answer as correct
        setChoices(
          matchingResult.data.choices.map(choice => ({
            ...choice,
            correct: true,
          })),
        );
        const answerPairs = [];
        matchingResult.data.choices.forEach((answer, index) => {
          const matchingOption = matchingResult.data.matches.find(
            match => match.id === answer.id,
          );
          answerPairs[index] = matchingOption;
        });
        setPairs(answerPairs);
        setIsShowingResults(true);
      } else if (
        matchingResult.data?.givenAnswer &&
        xor(
          matchingResult.data.givenAnswer,
          orderedCorrectChoices.map(choice => choice.id),
        ).length === 0
      ) {
        // Question was previously answered and question has not changed
        const givenAnswerChoices = [];
        const givenAnswerPairs = [];
        matchingResult.data.givenAnswer.forEach((answer, index) => {
          const matchingItem = orderedCorrectChoices[index];
          const matchingOption = matchingResult.data.matches.find(
            match => match.id === answer,
          );
          const correctChoice = orderedCorrectChoices[index];
          givenAnswerChoices.push({
            ...matchingItem,
            // Was the given answer last time correct
            correct: compareMatchIdOrText(
              matchingResult.data.matches,
              matchingOption,
              correctChoice.id,
            ),
          });
          givenAnswerPairs[index] = matchingOption;
        });
        setChoices(givenAnswerChoices);
        setPairs(givenAnswerPairs);
        setIsShowingResults(true);
      } else {
        setChoices(matchingResult?.data?.choices);
        setMatches(matchingResult?.data?.matches);
      }
    }
  }, [error, loading, matchingResult]);

  const checkAnswer = () => {
    if (isEmpty(pairs)) return;
    // This will first check if the answers are correct
    // then parse through the answer array to mark as correct or incorrect
    // Before finally recording the attempt
    request
      .postJSON(matchingResult?.meta?.saveUrl, {
        answer: pairs.map(choice => choice.id),
        question_id: questionId,
      })
      .then(response => {
        let newChoices = [];
        const { correct_answer } = response.answer.answer;
        setChoices(prevChoices => {
          newChoices = prevChoices.map((choice, index) => ({
            ...choice,
            correct: compareMatchIdOrText(
              pairs,
              pairs[index],
              correct_answer[index],
            ),
          }));
          return newChoices;
        });
        setIsShowingResults(true);
      })
      .catch(() => {
        // eslint-disable-next-line no-alert
        alert(t('questions.question.knowledge_check.error'));
        window.location.reload();
      });
  };

  function tryAgain() {
    setPairs([]);
    setMatches(matchingResult?.data?.matches);
    setIsShowingResults(false);
  }

  const MatchingChoiceLoader = () => (
    <div>
      <div className="mb-12">
        <Skeleton height={52} width="100%" />
      </div>
      <div className="mb-12">
        <Skeleton height={52} width="100%" />
      </div>
      <div className="mb-12">
        <Skeleton height={52} width="100%" />
      </div>
      <div className="mb-12">
        <Skeleton height={52} width="100%" />
      </div>
    </div>
  );

  if (error) {
    return <Error />;
  }

  if (loading) {
    return (
      <div>
        <div className="mb-24">
          <Heading component="h5" variant={HEADING_VARIANTS.H5}>
            <Skeleton width="75%" />
          </Heading>
        </div>
        <MatchingChoiceLoader />
      </div>
    );
  }

  const isActiveAriaLabel =
    active !== undefined
      ? t('learning.lessons.assistive_text.matching.match_option', {
          item: matchingResult.data.choices[active].item,
        })
      : '';

  if (choices.length > 0) {
    return (
      <div>
        <div className="KnowledgeCheckShow">
          <div className="Label Label--brand CustomColor--backgroundColor ">
            {t('questions.question.knowledge_check.badge')}
          </div>
          <QuestionElementPrompt
            elementHeaderClass={`${matchingResult.meta.elementHeaderClass} mt-0`}
            isGradable={matchingResult.data.isGradable}
            isPractice={matchingResult.data.isPractice}
            labelErrorClass={matchingResult.meta.labelErrorClass}
            questionSubjectClass={`${matchingResult.meta.questionSubjectClass} mt-0 mb-16`}
            required={matchingResult.data.required}
            subject={matchingResult.data.subject}
          />
          <div className="mb-16">
            <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
              {t('questions.choices.matching.match_pair')}
            </Text>
          </div>
          <div>{isShowingResults && calculatedScore(choices)}</div>
        </div>
        <AssistiveText
          assistiveStatus={assistiveStatus}
          questionId={questionId}
          matchingResult={matchingResult}
        />
        <div
          className={`MatchingShow ${choiceClassNames}`}
          aria-controls={`matchingInfo-${questionId}`}
        >
          <ItemChoice
            questionId={questionId}
            pairs={pairs}
            setPairs={setPairs}
            active={active}
            onClickItemChoice={onClickItemChoice}
            choices={choices}
            removeMatch={removeMatch}
            addMatch={addMatch}
            replaceMatch={replaceMatch}
            isShowingResults={isShowingResults}
            setAssistiveStatus={setAssistiveStatus}
          />
          <MatchingChoice
            questionId={questionId}
            setPairs={setPairs}
            active={active}
            removeMatch={removeMatch}
            matches={matches}
            setAssistiveStatus={setAssistiveStatus}
            isActiveAriaLabel={isActiveAriaLabel}
          />
        </div>
        <div className="d-f fd-rr mt-16">
          {isShowingResults ? (
            <Button
              type="button"
              disabled={!choices.some(c => c.correct === false)}
              onClick={tryAgain}
              icon="refresh"
            >
              {t('questions.question.knowledge_check.try_again')}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={
                isEmpty(pairs) ||
                pairs.length !== choices.length ||
                pairs.some(el => el === null)
              }
              onClick={checkAnswer}
            >
              {t('questions.question.knowledge_check.check_answer')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return <Error />;
};

export default MatchingShow;
